export const OPERATING_COST_STORAGE_KEY = "tradvera.operating-cost-ledger.v1";

export type OperatingCostMode = "free" | "fixed" | "usage" | "one_time" | "unknown";
export type OperatingCostFundingSource = "project_operations" | "trading_capital";

export type OperatingCostEntry = {
  id: string;
  displayName: string;
  category: string;
  mode: OperatingCostMode;
  billingCycle: "monthly" | "annual" | "usage" | "one_time";
  amount: number | null;
  currency: string;
  subscriptionStatus: "trial" | "active" | "cancelled" | "inactive" | "unknown";
  fundingSource: OperatingCostFundingSource;
  renewalDate: string | null;
  autoRenew: boolean;
  knownAt: string;
};

export type OperatingCostLedger = {
  schemaVersion: "tradvera.operating-cost-ledger.v1";
  generatedAt: string;
  entries: OperatingCostEntry[];
  monthlyFixedByCurrency: Record<string, number>;
  monthlyUsageByCurrency: Record<string, number>;
  tradingCapitalChargeByCurrency: Record<string, number>;
  projectOperationsChargeByCurrency: Record<string, number>;
  unknownCostEntryIds: string[];
  completeness: "complete" | "partial";
};

export type OperatingCostInput = Omit<OperatingCostEntry, "amount" | "currency" | "knownAt"> & {
  amount?: number | null;
  currency?: string | null;
  knownAt?: string | null;
};

function round(value: number) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function add(bucket: Record<string, number>, currency: string, amount: number) {
  bucket[currency] = round((bucket[currency] ?? 0) + amount);
}

export function buildOperatingCostLedger(inputs: OperatingCostInput[], generatedAt: string): OperatingCostLedger {
  const entries = inputs.map<OperatingCostEntry>((input) => ({
    ...input,
    amount: Number.isFinite(input.amount) && Number(input.amount) >= 0 ? Number(input.amount) : null,
    currency: String(input.currency || "USD").toUpperCase(),
    knownAt: input.knownAt ?? generatedAt,
  }));
  const monthlyFixedByCurrency: Record<string, number> = {};
  const monthlyUsageByCurrency: Record<string, number> = {};
  const tradingCapitalChargeByCurrency: Record<string, number> = {};
  const projectOperationsChargeByCurrency: Record<string, number> = {};
  const unknownCostEntryIds: string[] = [];

  for (const entry of entries) {
    if (entry.subscriptionStatus === "inactive" || entry.mode === "free") continue;
    if (entry.mode === "unknown" || entry.amount === null) {
      if (entry.subscriptionStatus === "active" || entry.subscriptionStatus === "trial" || entry.subscriptionStatus === "cancelled") unknownCostEntryIds.push(entry.id);
      continue;
    }
    if (entry.mode === "one_time") continue;
    const monthlyEquivalent = entry.billingCycle === "annual" ? entry.amount / 12 : entry.amount;
    if (entry.mode === "usage") add(monthlyUsageByCurrency, entry.currency, monthlyEquivalent);
    else add(monthlyFixedByCurrency, entry.currency, monthlyEquivalent);
    add(entry.fundingSource === "trading_capital" ? tradingCapitalChargeByCurrency : projectOperationsChargeByCurrency, entry.currency, monthlyEquivalent);
  }

  return {
    schemaVersion: "tradvera.operating-cost-ledger.v1",
    generatedAt,
    entries,
    monthlyFixedByCurrency,
    monthlyUsageByCurrency,
    tradingCapitalChargeByCurrency,
    projectOperationsChargeByCurrency,
    unknownCostEntryIds,
    completeness: unknownCostEntryIds.length ? "partial" : "complete",
  };
}

export function monthlyOperatingCostInCurrency(ledger: OperatingCostLedger, currency: string) {
  const code = currency.toUpperCase();
  return round((ledger.monthlyFixedByCurrency[code] ?? 0) + (ledger.monthlyUsageByCurrency[code] ?? 0));
}
